import {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import type { RootState, AppDispatch } from '../redux/store'

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export const usePageTitle = (path: string): void => {
  const { t } = useTranslation();
   useEffect(() => {
       document.title = t(path);
  }, [path, t])
};

export const useScript = (url: string, async: boolean = false, onLoad?: () => void): void => {
    useEffect(() => {
        const script = document.createElement('script');

        script.src = url;
        script.async = async;
        if (onLoad) script.onload = onLoad;

        document.body.appendChild(script);

        return () => {
            document.body.removeChild(script);
        }
    }, [url, async, onLoad]);
};

export const useDebouncedEffect = (effect: () => void, deps: any[], delay: number) => {
    useEffect(() => {
        const handler = setTimeout(() => effect(), delay);

        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [...(deps || []), delay]);
};

export interface ISize {
    width: number,
    height: number,
}

export const useSize = (elementId?: string) => {
    const [size, setSize] = useState<ISize>({
        width: 0,
        height: 0,
    });

    useEffect(() => {
        // Handler to call on window resize
        function handleResize() {
            // @ts-ignore
            const width = elementId ? document.getElementById(elementId).clientWidth : window.innerWidth;
            // @ts-ignore
            const height = elementId ? document.getElementById(elementId).clientHeight : window.innerHeight;

            // Set width/height to state
            setSize({width, height});
        }

        // Add event listener
        window.addEventListener("resize", handleResize);

        // Call handler right away so state gets updated with initial window size
        handleResize();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleResize);
    }, [elementId]);

    return size;
};

export const useScrollPosition = (elementId: string, containerId?: string) => {
    const [pageY, setPageY] = useState<number>(0);

    useEffect(() => {
        const container: any = containerId ? document.querySelector(`#${containerId}`) : window;
        if (!container) return;

        const containerOffset = containerId ? container.offsetTop : 0;

        function handleScroll() {
            const element:any = document.querySelector(`#${elementId}`);
            if (!element) return;

            const elementOffset = element.offsetTop;
            const elementHeight = element.offsetHeight;
            const scrollY = containerId ? container.scrollTop : container.scrollY;

            const elementPosition = containerOffset + elementOffset - scrollY - elementHeight;

            setPageY(elementPosition);
        }

        // @ts-ignore
        container.addEventListener("scroll", handleScroll);

        handleScroll();

        // @ts-ignore
        return () => container.removeEventListener("scroll", handleScroll);
    }, [elementId, containerId]);

    return pageY;
};
